import * as React from 'react'
import { useViewTransitionNavigate } from '../../hooks/useViewTransitionNavigate.ts'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faKey } from '@fortawesome/free-solid-svg-icons/faKey'
import { faCreditCard } from '@fortawesome/free-solid-svg-icons/faCreditCard'
import { faPen } from '@fortawesome/free-solid-svg-icons/faPen'
import { faPhone } from '@fortawesome/free-solid-svg-icons/faPhone'
import { faGlobe } from '@fortawesome/free-solid-svg-icons/faGlobe'
import { faCommentSms } from '@fortawesome/free-solid-svg-icons/faCommentSms'
import { faLocationDot } from '@fortawesome/free-solid-svg-icons/faLocationDot'
import { useSnapshot } from 'valtio'
import { controlState } from '../../store/controlState.ts'
import { dataState } from '../../store/dataState.ts'
import { ManagePaymentsPopup } from '../../components/common/ManagePayments/ManagePaymentsPopup.tsx'
import { PopupPortal } from '../../components/common/Popup/PopupPortal.tsx'
import { ProfileForm } from './ProfileForm.tsx'

export const ProfileView: React.FC = () => {
    const profile = (useSnapshot(dataState) as typeof dataState).account
    const timezones = (useSnapshot(dataState) as typeof dataState).timeZones
    const { navigate } = useViewTransitionNavigate()
    const isMobile = useSnapshot(controlState).isOnMobileDevice
    const [paymentsOpen, setPaymentsOpen] = React.useState(false)
    const [editOpen, setEditOpen] = React.useState(false)

    const cancel = () => {
        navigate('/' + controlState.currentPage)
    }

    const tzRecord = timezones.find(t => t._id === profile.timeZone)
    const tzName = tzRecord?.timezoneName || profile.timeZone || '—'

    // Format phone number for display based on country
    const formatPhone = (phone: string | undefined, country: string | undefined) => {
        if (!phone) return '—'
        const digits = phone.replace(/\D/g, '')
        if ((country === 'US' || country === 'USA' || country === 'CA') && digits.length === 10) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
        }
        return phone
    }

    const formattedPhone = formatPhone(profile.mobile, profile.homeAddress?.country)

    const initials = `${(profile.firstName || '?')[0]}${(profile.lastName || '?')[0]}`.toUpperCase()

    const address = profile.homeAddress
    const addressLine = [
        address?.line1,
        address?.line2,
    ].filter(Boolean).join(', ')
    const cityLine = [
        address?.city,
        address?.state,
        address?.postalCode,
    ].filter(Boolean).join(', ')
    const countryLabel = address?.country === 'CA' ? 'Canada' : address?.country === 'US' || address?.country === 'USA' ? 'United States' : address?.country || ''

    const width = !isMobile ? 'max-w-[38rem]' : 'w-full'

    return (<>
        <div className={`flex flex-col gap-4 ${width} mx-auto pb-6 px-3 max-h-[90vh] overflow-y-auto`}>
            {/* Header */}
            <div className={'sticky top-0 z-10 flex flex-col bg-base-100 pt-6 pb-2 gap-2'}>
                <div className={'flex items-center justify-between'}>
                    <h1 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-primary m-0`}>Profile</h1>
                    <button type="button" className={'btn btn-ghost btn-sm text-primary'} onClick={cancel}>
                        Close
                    </button>
                </div>
                <p className={'text-sm text-gray-500 m-0 text-left'}>Manage your account details</p>
                <div className={'flex items-center gap-2'}>
                    <button
                        type="button"
                        className={'btn btn-secondary btn-sm gap-2'}
                        onClick={() => setEditOpen(true)}
                    >
                        <FontAwesomeIcon icon={faPen} />
                        Edit Profile
                    </button>
                    <button
                        type="button"
                        className={'btn btn-ghost btn-sm gap-1 text-secondary'}
                        onClick={() => setPaymentsOpen(true)}
                    >
                        <FontAwesomeIcon icon={faCreditCard} />
                        Payments
                    </button>
                    <button
                        type="button"
                        className={'btn btn-ghost btn-sm gap-1 text-secondary'}
                        onClick={() => controlState.changePassword = true}
                    >
                        <FontAwesomeIcon icon={faKey} />
                        Password
                    </button>
                </div>
            </div>

            {/* Identity card */}
            <div className={'bg-primary text-primary-content rounded-xl p-4 flex items-center gap-4'}>
                <div className={'w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-xl font-bold shrink-0'}>
                    {initials}
                </div>
                <div className={'flex-1 min-w-0'}>
                    <div className={'text-xl font-bold'}>{profile.firstName} {profile.lastName}</div>
                    <div className={'text-sm opacity-70'}>{profile.email}</div>
                </div>
            </div>

            {/* Contact & Preferences card */}
            <div className={'bg-primary text-primary-content rounded-xl p-4 flex flex-col gap-3'}>
                <div className={'text-xs uppercase tracking-wide opacity-60 font-medium'}>Contact & Preferences</div>

                <div className={'flex items-center gap-3'}>
                    <FontAwesomeIcon icon={faPhone} className={'opacity-60 w-4'} />
                    <span className={'font-medium'}>{formattedPhone}</span>
                </div>

                <div className={'flex items-center gap-3'}>
                    <FontAwesomeIcon icon={faGlobe} className={'opacity-60 w-4'} />
                    <span className={'font-medium'}>{tzName}</span>
                </div>

                <div className={'flex items-center gap-3'}>
                    <FontAwesomeIcon icon={faCommentSms} className={'opacity-60 w-4'} />
                    <span className={'font-medium'}>
                        SMS Alerts: {profile.smsOptIn === 'Opt-in' ? '✓ Yes' : 'No'}
                    </span>
                </div>
            </div>

            {/* Address card */}
            <div className={'bg-primary text-primary-content rounded-xl p-4 flex flex-col gap-2'}>
                <div className={'text-xs uppercase tracking-wide opacity-60 font-medium'}>Address</div>

                <div className={'flex items-start gap-3'}>
                    <FontAwesomeIcon icon={faLocationDot} className={'opacity-60 w-4 mt-1'} />
                    <div className={'flex flex-col font-medium'}>
                        {addressLine && <span>{addressLine}</span>}
                        {cityLine && <span>{cityLine}</span>}
                        {countryLabel && <span>{countryLabel}</span>}
                        {!addressLine && !cityLine && <span>—</span>}
                    </div>
                </div>
            </div>
        </div>

        {/* Edit Profile popup */}
        <PopupPortal open={editOpen}>
            <div className={'w-full max-h-[90vh] overflow-y-auto'}>
                <ProfileForm onClose={() => setEditOpen(false)} />
            </div>
        </PopupPortal>

        <ManagePaymentsPopup open={paymentsOpen} setOpen={setPaymentsOpen} />
    </>)
}
